import { useTranslation } from "react-i18next";
import { formatMonthShort } from "../../lib/format";
import type { VacancyPeriod } from "../../lib/realestate";

interface VacancyCardProps {
  vacancy: VacancyPeriod;
  onAddReason: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function VacancyCard({ vacancy, onAddReason, onEdit, onDelete }: VacancyCardProps) {
  const { t } = useTranslation();
  const isOpen = vacancy.endMonth === null;
  const dateLabel = isOpen
    ? t("realestate.vacancy.card_open", { date: formatMonthShort(vacancy.startMonth) })
    : t("realestate.vacancy.card_closed", {
        start: formatMonthShort(vacancy.startMonth),
        end: formatMonthShort(vacancy.endMonth as string),
      });

  const durationCount = vacancy.durationMonths ?? 0;
  const durationLabel = isOpen
    ? t("realestate.vacancy.duration_ongoing")
    : t(
        durationCount === 1
          ? "realestate.vacancy.duration_one"
          : "realestate.vacancy.duration_other",
        { count: durationCount },
      );

  return (
    <div className="bg-white border rounded p-4 space-y-2">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-semibold">{vacancy.unitLabel}</p>
          <p className="text-sm text-slate-600">{dateLabel}</p>
          <p className="text-xs text-slate-500">{durationLabel}</p>
        </div>
        {isOpen && (
          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded">
            {t("realestate.vacancy.duration_ongoing")}
          </span>
        )}
      </div>

      {vacancy.annotation ? (
        <div className="border-t pt-2 space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">
              {t(`realestate.vacancy.reason_${vacancy.annotation.reason}`)}
            </span>
            <div className="flex gap-2 text-xs">
              <button type="button" className="text-blue-600 underline" onClick={onEdit}>
                {t("realestate.vacancy.edit_annotation")}
              </button>
              <button type="button" className="text-red-600 underline" onClick={onDelete}>
                {t("realestate.vacancy.delete_annotation")}
              </button>
            </div>
          </div>
          {vacancy.annotation.notes && (
            <p className="text-xs text-slate-600">{vacancy.annotation.notes}</p>
          )}
        </div>
      ) : (
        <button type="button" className="text-xs text-blue-600 underline" onClick={onAddReason}>
          {t("realestate.vacancy.add_reason")}
        </button>
      )}
    </div>
  );
}
